import React, { Component, ErrorInfo, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { KinshipRecord } from '@/lib/mock-data';
import { AlertTriangle, CheckCircle2, GitMerge, Users, ArrowRight, Database, Box, Layers } from 'lucide-react';
import { Kinship3DView } from './kinship-3d-view';
import { generateDynamicPermutations, KinshipPair, Permutation, PermutationOptions, AgeProfile } from '@/lib/permutation-engine';
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ReactFamilyTree from 'react-family-tree';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Info } from 'lucide-react';
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface TreeErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  permLabel?: string;
}

interface TreeErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class TreeErrorBoundary extends Component<TreeErrorBoundaryProps, TreeErrorBoundaryState> {
  constructor(props: TreeErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): TreeErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('TreeErrorBoundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-center p-4">
          <AlertTriangle className="w-8 h-8 text-amber-500 mb-2" />
          <div className="text-sm font-medium text-slate-700">Layout Error</div>
          <div className="text-xs text-slate-500 mt-1 max-w-[200px]">
            Unable to render this pedigree structure. The tree layout algorithm encountered an error.
          </div>
          {this.props.permLabel && (
            <div className="text-[10px] text-slate-400 mt-2 font-mono">
              {this.props.permLabel}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

const WIDTH = 160;
const HEIGHT = 140;

interface RegionalAgeProfile {
  id: string;
  label: string;
  motherAge: number;
  fatherAge: number;
  maxSiblingGap: number;  // Maximum age difference between siblings
  description: string;
  examples: string;
}

const REGIONAL_AGE_PROFILES: RegionalAgeProfile[] = [
  {
    id: 'none',
    label: 'No Age Data',
    motherAge: 0,
    fatherAge: 0,
    maxSiblingGap: 0,
    description: 'Age information is unavailable. All reverse directional hypotheses will be generated.',
    examples: 'Default when ages are unknown'
  },
  {
    id: 'modern_developed',
    label: 'Modern Developed Regions',
    motherAge: 29.9,
    fatherAge: 33.2,
    maxSiblingGap: 10,
    description: 'Highly industrialized and welfare-oriented regions with delayed parenthood due to education, career, and strong social policies.',
    examples: 'USA, Canada, UK, France, Germany, Japan, South Korea, Sweden'
  },
  {
    id: 'traditional_transitional',
    label: 'Traditional - Transitional Economies',
    motherAge: 24.6,
    fatherAge: 29,
    maxSiblingGap: 12,
    description: 'Regions shifting toward later marriage and urbanization, often influenced by economic development and changing social norms.',
    examples: 'Jordan, Turkey, Brazil, Mexico'
  },
  {
    id: 'traditional_early',
    label: 'Traditional - Early Marriage',
    motherAge: 22,
    fatherAge: 27,
    maxSiblingGap: 12,
    description: 'Regions with strong cultural norms for early marriage and family formation, typically rural or conservative societies.',
    examples: 'India, Pakistan, Afghanistan, some African countries'
  },
  {
    id: 'developing_urban',
    label: 'Developing - Emerging Urban',
    motherAge: 20.4,
    fatherAge: 26,
    maxSiblingGap: 12,
    description: 'Developing regions with growing urban influence and slightly delayed parenthood compared to rural counterparts.',
    examples: 'Nigeria, Kenya, China'
  },
  {
    id: 'developing_rural',
    label: 'Developing - High Fertility Rural',
    motherAge: 18.5,
    fatherAge: 23,
    maxSiblingGap: 15,
    description: 'Rural, resource-limited areas with very early motherhood and high fertility rates, limited access to healthcare and education.',
    examples: 'Niger, Chad, Mali'
  }
];

interface FamilyNodeData {
  id: string;
  gender: 'male' | 'female';
  parents: readonly { type: 'blood' | 'adopted', id: string }[];
  children: readonly { type: 'blood' | 'adopted', id: string }[];
  siblings: readonly { type: 'blood' | 'adopted', id: string }[];
  spouses: readonly { type: 'married' | 'unmarried', id: string }[];
  data?: {
    name: string;
    label?: string;
    isIndex?: boolean;
    isUntested?: boolean;
    age?: string | null;
  };
}

function ReactFamilyNode({ node, style }: { node: any, style: React.CSSProperties }) {
  const data = node.data as FamilyNodeData['data'];
  const isMale = node.gender === 'male';
  const isIndex = data?.isIndex;
  const isUntested = data?.isUntested;
  const age = data?.age;
  const hasValidAge = age && age !== 'Unspecified' && age !== 'Unknown';

  return (
    <div style={style} className="flex justify-center items-center pointer-events-none">
      <div className="flex flex-col items-center relative z-10">
        <div 
          className={cn(
            "w-12 h-12 border-2 flex items-center justify-center shadow-sm transition-all bg-background",
            isMale ? "rounded-none" : "rounded-full",
            isIndex 
              ? "bg-primary text-primary-foreground border-primary" 
              : "bg-background text-foreground border-border",
            isUntested && "opacity-60 grayscale bg-slate-100 border-dashed"
          )}
        >
          <span className="text-xs font-bold">{data?.name.split(' ').map(n => n[0]).join('').substring(0, 2)}</span>
        </div>
        <div className="mt-2 text-center bg-white/90 backdrop-blur-sm px-2 py-1 rounded border border-slate-100 shadow-sm max-w-[140px]">
          <div className="text-[10px] font-bold text-slate-700 truncate" title={data?.name}>
            {data?.name}
            {hasValidAge && <span className="ml-1 text-blue-600 font-normal">({age})</span>}
          </div>
          {data?.label && <div className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">{data.label}</div>}
        </div>
      </div>
    </div>
  );
}


interface PedigreeNodeProps {
  name: string;
  gender: 'M' | 'F';
  isIndex?: boolean;
  label?: string;
  className?: string;
}

function PedigreeNode({ name, gender, isIndex, label, className }: PedigreeNodeProps) {
  return (
    <div className={cn("flex flex-col items-center z-10 relative p-2", className)}>
      <div 
        className={cn(
          "w-12 h-12 border-2 flex items-center justify-center shadow-sm transition-all hover:scale-105 bg-background",
          gender === 'M' ? "rounded-none" : "rounded-full",
          isIndex 
            ? "bg-primary text-primary-foreground border-primary" 
            : "bg-background text-foreground border-border",
          gender === 'M' && !isIndex && "border-foreground/40 text-foreground/60",
          gender === 'F' && !isIndex && "border-foreground/40 text-foreground/60"
        )}
      >
        <span className="text-xs font-bold">{name.split(' ').map(n => n[0]).join('').substring(0, 2)}</span>
      </div>
      <div className="mt-1 text-center bg-background/80 backdrop-blur-sm px-2 rounded-full border border-transparent max-w-[100px]">
        <div className="text-[10px] font-medium truncate" title={name}>{name}</div>
        {label && <div className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</div>}
      </div>
    </div>
  );
}

export function ParentChildTree({ parent, child }: { parent: PedigreeNodeProps, child: PedigreeNodeProps }) {
  return (
    <div className="flex flex-col items-center gap-8 relative py-8">
      <div className="absolute top-[4rem] bottom-[4rem] w-px bg-foreground/80 -z-0" />
      <PedigreeNode {...parent} />
      <PedigreeNode {...child} />
    </div>
  );
}

export function FullSiblingTree({ sibling1, sibling2 }: { sibling1: PedigreeNodeProps, sibling2: PedigreeNodeProps }) {
  return (
    <div className="flex flex-col items-center gap-6 relative py-4">
       <div className="flex gap-24 mb-4 relative">
         <div className="absolute top-[1.5rem] left-[1.5rem] right-[1.5rem] h-px bg-foreground/80 -z-0" />
         <div className="absolute top-[1.5rem] left-1/2 h-8 w-px bg-foreground/80 -z-0" />
         <PedigreeNode name="?" gender="M" label="Father" />
         <PedigreeNode name="?" gender="F" label="Mother" />
       </div>
       <div className="relative w-48 h-8">
          <div className="absolute top-0 left-0 right-0 h-px bg-foreground/80" />
          <div className="absolute top-0 left-0 h-8 w-px bg-foreground/80" />
          <div className="absolute top-0 right-0 h-8 w-px bg-foreground/80" />
          <div className="absolute -top-4 left-1/2 h-4 w-px bg-foreground/80" />
       </div>
       <div className="flex gap-16 -mt-2">
         <PedigreeNode {...sibling1} />
         <PedigreeNode {...sibling2} />
       </div>
    </div>
  );
}

export function TrioTree({ father, mother, child }: { father: PedigreeNodeProps, mother: PedigreeNodeProps, child: PedigreeNodeProps }) {
  return (
    <div className="flex flex-col items-center gap-8 relative py-8">
      {/* Parents Row */}
      <div className="flex gap-16 relative z-10">
        {/* Mating Line */}
        <div className="absolute top-[1.5rem] left-[2rem] right-[2rem] h-px bg-foreground/80 -z-10" />
        {/* Descent Line */}
        <div className="absolute top-[1.5rem] left-1/2 h-16 w-px bg-foreground/80 -z-10" />
        
        <PedigreeNode {...father} label="Father" />
        <PedigreeNode {...mother} label="Mother" />
      </div>

      {/* Child Row */}
      <div className="mt-2">
        <PedigreeNode {...child} label="Child" />
      </div>
    </div>
  );
}

// New Pass 1 Combinatorial List View
interface Pass1CombinatorialListProps {
  kinshipRecords?: KinshipRecord[];
}

export function Pass1CombinatorialList({ kinshipRecords }: Pass1CombinatorialListProps) {
  // Use case-specific kinship records - no fallback to mock data
  const records = kinshipRecords || [];
  
  // Transform kinship data into flat list of LR > 1 relationship hypotheses
  // Business Rule 1: Only include hypotheses with LR > 1 (supports hypothesis over unrelated null)
  const hypotheses = records.flatMap((record) => {
    const items = [];
    
    // LR > 1 indicates support for the hypothesis over the null (unrelated)
    if (record.parentChildLR > 1) {
      items.push({
        pair: `${record.relative1.name} & ${record.relative2.name}`,
        type: 'Parent/Child',
        lr: record.parentChildLR,
        details: 'Direct lineage relationship'
      });
    }
    
    if (record.fullSiblingLR > 1) {
      items.push({
        pair: `${record.relative1.name} & ${record.relative2.name}`,
        type: 'Full Sibling',
        lr: record.fullSiblingLR,
        details: 'Shared biological parents'
      });
    }
    
    if (record.extendedFamilyLR > 1) {
      items.push({
        pair: `${record.relative1.name} & ${record.relative2.name}`,
        type: 'Extended Family',
        lr: record.extendedFamilyLR,
        details: '2nd Degree (Uncle/Aunt/Grandparent)'
      });
    }
    
    return items;
  });

  return (
    <div className="space-y-6">
      <div className="bg-muted/50 p-4 rounded-lg border border-border flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Database className="w-5 h-5 text-muted-foreground" />
          <div>
            <h3 className="font-semibold text-sm">Total Combinatorial Hypotheses</h3>
            <p className="text-xs text-muted-foreground">Derived from non-zero LR values in kinship matrix</p>
          </div>
        </div>
        <Badge variant="secondary" className="text-lg px-4 py-1">{hypotheses.length}</Badge>
      </div>

      <div className="rounded-md border border-border overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead>Pair</TableHead>
              <TableHead>Hypothesis Type</TableHead>
              <TableHead className="text-right">Likelihood Ratio (LR)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hypotheses.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell className="font-mono text-xs text-muted-foreground">{idx + 1}</TableCell>
                <TableCell className="font-medium">{item.pair}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={cn(
                    "font-normal",
                    item.type === 'Parent/Child' && "bg-green-50 text-green-700 border-green-200",
                    item.type === 'Full Sibling' && "bg-blue-50 text-blue-700 border-blue-200",
                    item.type === 'Extended Family' && "bg-amber-50 text-amber-700 border-amber-200",
                  )}>
                    {item.type}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono">
                  {item.lr >= 1000 ? item.lr.toExponential(2) : item.lr.toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      <div className="flex justify-center pt-2">
         <p className="text-sm text-muted-foreground text-center max-w-md">
           These <strong>{hypotheses.length} pairwise hypotheses</strong> form the raw input for pedigree reconstruction, where biological constraints (e.g. max 2 parents) filter invalid combinations.
         </p>
      </div>
    </div>
  );
}

interface ComprehensiveFamilyTreeProps {
  kinshipRecords?: KinshipRecord[];
}

export function ComprehensiveFamilyTree({ kinshipRecords }: ComprehensiveFamilyTreeProps) {
  const [viewMode, setViewMode] = React.useState<'total' | 'high'>('total');
  const [displayMode, setDisplayMode] = React.useState<'2d' | '3d'>('2d');
  const [permutationFilter, setPermutationFilter] = React.useState<'all' | 'parentChild' | 'fullSibling' | 'extended'>('all');
  const [selectedRegion, setSelectedRegion] = React.useState<string>('none');
  const [currentPermutationIndex, setCurrentPermutationIndex] = React.useState<number>(0);
  const [suppressCousins, setSuppressCousins] = React.useState<boolean>(true);
  
  const selectedAgeProfile = React.useMemo(() => 
    REGIONAL_AGE_PROFILES.find(p => p.id === selectedRegion) || REGIONAL_AGE_PROFILES[0],
  [selectedRegion]);
  
  // Convert KinshipRecords to KinshipPairs for the permutation engine
  // No fallback - uses only case-specific data
  const kinshipPairs: KinshipPair[] = React.useMemo(() => {
    if (!kinshipRecords || kinshipRecords.length === 0) return [];
    return kinshipRecords.map(record => ({
      relative1: {
        id: record.relative1.id,
        name: record.relative1.name,
        gender: record.relative1.gender,
        age: record.relative1.age
      },
      relative2: {
        id: record.relative2.id,
        name: record.relative2.name,
        gender: record.relative2.gender,
        age: record.relative2.age
      },
      relationship: record.relationship,
      parentChildLR: record.parentChildLR,
      fullSiblingLR: record.fullSiblingLR,
      extendedFamilyLR: record.extendedFamilyLR
    }));
  }, [kinshipRecords]);
  
  // Generate dynamic permutations from the case-specific kinship data
  // Uses suppressCousins option to control whether cousin scenarios are included
  // Uses ageProfile for age-based generational filtering
  // Automatically uses Best-Fit mode when strict mode returns 0 permutations
  const { dynamicPermutations, isBestFitMode } = React.useMemo(() => {
    if (kinshipPairs.length === 0) return { dynamicPermutations: [], isBestFitMode: false };
    
    // Convert selected age profile to the engine's format
    const ageProfileForEngine: AgeProfile | undefined = selectedAgeProfile.id !== 'none' 
      ? {
          id: selectedAgeProfile.id,
          label: selectedAgeProfile.label,
          motherAge: selectedAgeProfile.motherAge,
          fatherAge: selectedAgeProfile.fatherAge,
          maxSiblingGap: selectedAgeProfile.maxSiblingGap
        }
      : undefined;
    
    // First, try strict mode (default)
    const strictPermutations = generateDynamicPermutations(kinshipPairs, { 
      suppressCousins,
      ageProfile: ageProfileForEngine
    });
    
    // If strict mode returns permutations, use them
    if (strictPermutations.length > 0) {
      return { dynamicPermutations: strictPermutations, isBestFitMode: false };
    }
    
    // If strict mode returns 0 permutations, try Best-Fit mode
    // Best-Fit mode handles conflicting evidence by prioritizing PC > FS > EXT
    const bestFitPermutations = generateDynamicPermutations(kinshipPairs, { 
      suppressCousins,
      ageProfile: ageProfileForEngine,
      bestFitMode: true
    });
    
    return { dynamicPermutations: bestFitPermutations, isBestFitMode: bestFitPermutations.length > 0 };
  }, [kinshipPairs, suppressCousins, selectedAgeProfile]);

  // Use ONLY dynamic permutations from case-specific kinship data
  // All permutations are generated dynamically based on the uploaded case's kinship matrix
  const totalPermutations = React.useMemo(() => {
    return dynamicPermutations;
  }, [dynamicPermutations]);
  
  // Filter permutations based on selected filter
  const filteredPermutations = React.useMemo(() => {
    if (permutationFilter === 'all') return totalPermutations;
    return totalPermutations.filter(perm => perm.category === permutationFilter);
  }, [totalPermutations, permutationFilter]);
  
  // Reset permutation index when category filter changes
  React.useEffect(() => {
    setCurrentPermutationIndex(0);
  }, [permutationFilter]);
  
  // Navigation handlers for 3D view
  const handlePrevPermutation = React.useCallback(() => {
    setCurrentPermutationIndex(prev => 
      prev <= 0 ? filteredPermutations.length - 1 : prev - 1
    );
  }, [filteredPermutations.length]);
  
  const handleNextPermutation = React.useCallback(() => {
    setCurrentPermutationIndex(prev => 
      prev >= filteredPermutations.length - 1 ? 0 : prev + 1
    );
  }, [filteredPermutations.length]);
  
  // Count permutations by category
  const permutationCounts = React.useMemo(() => ({
    all: totalPermutations.length,
    parentChild: totalPermutations.filter(p => p.category === 'parentChild').length,
    fullSibling: totalPermutations.filter(p => p.category === 'fullSibling').length,
    extended: totalPermutations.filter(p => p.category === 'extended').length,
  }), [totalPermutations]);

  return (
    <div className="flex flex-col items-center p-4 bg-slate-50/50 rounded-xl border border-slate-200 h-full">
      <h4 className="text-xs font-bold text-slate-500 mb-4 uppercase tracking-widest flex items-center gap-2">
        <GitMerge className="w-3 h-3" />
        Reconstructed Global Pedigrees
      </h4>

      {/* Regional Age Profile Selector */}
      <div className="w-full mb-3 flex items-center gap-2 justify-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Info className="w-3 h-3" />
                <span>Age Region:</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">When age information is available, selecting a region helps determine parent/child directionality and reduces implausible reverse hypotheses.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Select value={selectedRegion} onValueChange={setSelectedRegion}>
          <SelectTrigger className="w-[260px] h-8 text-xs" data-testid="select-age-region">
            <SelectValue placeholder="Select age region" />
          </SelectTrigger>
          <SelectContent>
            {REGIONAL_AGE_PROFILES.map((profile) => (
              <TooltipProvider key={profile.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SelectItem value={profile.id} className="text-xs cursor-pointer">
                      <div className="flex items-center gap-2">
                        <span>{profile.label}</span>
                        {profile.id !== 'none' && (
                          <span className="text-[10px] text-slate-400">
                            (M:{profile.motherAge} / F:{profile.fatherAge})
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <div className="space-y-1">
                      <p className="text-xs font-medium">{profile.label}</p>
                      <p className="text-xs text-slate-300">{profile.description}</p>
                      {profile.id !== 'none' && (
                        <>
                          <p className="text-[10px] text-slate-400">Avg Mother Age: {profile.motherAge} | Avg Father Age: {profile.fatherAge}</p>
                          <p className="text-[10px] text-amber-400">Max Sibling Gap: {profile.maxSiblingGap} years</p>
                          <p className="text-[10px] text-emerald-400">Examples: {profile.examples}</p>
                        </>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </SelectContent>
        </Select>
        {selectedRegion !== 'none' && (
          <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
            Age-based filtering active
          </Badge>
        )}
      </div>

      {/* Best-Fit Mode Indicator */}
      {isBestFitMode && (
        <div className="w-full mb-3 flex items-center gap-2 justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300 px-2 py-0.5">
                  <Info className="w-3 h-3 mr-1" />
                  Best-Fit Mode Active
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="text-xs">Strict mode produced 0 permutations due to conflicting kinship evidence. Best-Fit mode prioritizes PC (parent-child) relationships over conflicting FS (full sibling) evidence to generate the most likely pedigree interpretation.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* Suppress Cousin Scenarios Checkbox */}
      <div className="w-full mb-3 flex items-center gap-2 justify-center">
        <div className="flex items-center gap-2">
          <Checkbox
            id="suppress-cousins"
            checked={suppressCousins}
            onCheckedChange={(checked) => setSuppressCousins(checked === true)}
            data-testid="checkbox-suppress-cousins"
          />
          <Label 
            htmlFor="suppress-cousins" 
            className="text-xs text-slate-600 cursor-pointer"
          >
            Suppress Cousin Scenarios
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-slate-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">When checked, cousin-type permutations (Extended Family Cousins, Disjointed Family) are hidden from the reconstruction list. Uncheck to include all cousin scenarios supported by the kinship data.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {!suppressCousins && (
          <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">
            Cousin scenarios included
          </Badge>
        )}
      </div>

      {/* View Mode Toggle and Filter Buttons */}
      <div className="w-full flex flex-wrap gap-1.5 mb-3 justify-center items-center">
        {/* 2D/3D Toggle */}
        <div className="flex rounded-lg border border-violet-200 overflow-hidden mr-3">
          <button
            onClick={() => setDisplayMode('2d')}
            className={cn(
              "px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5",
              displayMode === '2d'
                ? "bg-violet-600 text-white"
                : "bg-white text-violet-600 hover:bg-violet-50"
            )}
            data-testid="button-view-2d"
          >
            <Layers className="w-3.5 h-3.5" />
            2D
          </button>
          <button
            onClick={() => setDisplayMode('3d')}
            className={cn(
              "px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5",
              displayMode === '3d'
                ? "bg-violet-600 text-white"
                : "bg-white text-violet-600 hover:bg-violet-50"
            )}
            data-testid="button-view-3d"
          >
            <Box className="w-3.5 h-3.5" />
            3D
          </button>
        </div>

        {/* Category Filter Buttons */}
        <button
          onClick={() => setPermutationFilter('all')}
          className={cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition-all border",
            permutationFilter === 'all'
              ? "bg-slate-900 text-white border-slate-700 shadow-md"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          )}
          data-testid="button-filter-all"
        >
          All ({permutationCounts.all})
        </button>
        <button
          onClick={() => setPermutationFilter('parentChild')}
          className={cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition-all border",
            permutationFilter === 'parentChild'
              ? "bg-green-600 text-white border-green-700 shadow-md"
              : "bg-white text-green-700 border-green-200 hover:bg-green-50"
          )}
          data-testid="button-filter-parent-child"
        >
          Parent/Child ({permutationCounts.parentChild})
        </button>
        <button
          onClick={() => setPermutationFilter('fullSibling')}
          className={cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition-all border",
            permutationFilter === 'fullSibling'
              ? "bg-blue-600 text-white border-blue-700 shadow-md"
              : "bg-white text-blue-700 border-blue-200 hover:bg-blue-50"
          )}
          data-testid="button-filter-full-sibling"
        >
          Full Sibling ({permutationCounts.fullSibling})
        </button>
        <button
          onClick={() => setPermutationFilter('extended')}
          className={cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition-all border",
            permutationFilter === 'extended'
              ? "bg-amber-600 text-white border-amber-700 shadow-md"
              : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"
          )}
          data-testid="button-filter-extended"
        >
          Extended ({permutationCounts.extended})
        </button>
      </div>

      {/* Control Dashboard - Compact */}
      <div className="w-full bg-slate-900 text-slate-300 px-4 py-3 rounded-lg border border-slate-800 shadow-lg mb-4">
        <div className="flex items-center justify-between">
           <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-emerald-400" />
              <h4 className="text-xs font-bold text-white">Exhaustive Matrix Solutions</h4>
              <Badge variant="outline" className="border-slate-700 text-slate-400 text-[9px] h-5">Graph-Driven LR Enumeration</Badge>
           </div>
           <div className="flex items-center gap-3">
              <div className="text-2xl font-mono font-bold text-white">{filteredPermutations.length}</div>
              <div className="text-[9px] uppercase tracking-wider font-semibold text-emerald-400">
                {permutationFilter === 'all' ? 'Permutations' : 
                 permutationFilter === 'parentChild' ? 'Parent/Child' :
                 permutationFilter === 'fullSibling' ? 'Full Sibling' :
                 'Extended'}
              </div>
           </div>
        </div>
      </div>
      
      {/* Content Area - 2D Pedigree List or 3D View */}
      {displayMode === '3d' ? (
        <div className="w-full flex-1 min-h-[600px]">
          <Kinship3DView 
            kinshipRecords={kinshipRecords || []} 
            categoryFilter={permutationFilter}
            permutations={filteredPermutations}
            currentIndex={currentPermutationIndex}
            onPrev={handlePrevPermutation}
            onNext={handleNextPermutation}
          />
        </div>
      ) : (
        <div className="w-full flex-1 flex flex-col min-h-0">
            <div className="flex justify-center pb-2">
               <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 text-[10px]">
                  {permutationFilter === 'all' ? 'All' : permutationFilter === 'parentChild' ? 'Parent/Child' : permutationFilter === 'fullSibling' ? 'Full Sibling' : 'Extended'} ({filteredPermutations.length}) - Structural Topology
               </Badge>
            </div>
            <div className="flex flex-col gap-6 flex-1 overflow-y-auto pr-2">
                {filteredPermutations.map((perm, index) => (
                   <div key={perm.id} className="bg-white rounded-lg border border-slate-200 p-4 flex flex-col relative overflow-hidden hover:shadow-md transition-all min-h-[500px]">
                      <div className="flex justify-between items-center px-2 pb-2 border-b border-slate-100 mb-4 z-20 bg-white/80 backdrop-blur-sm">
                        <div className="flex items-center gap-2">
                           <Badge className="h-5 font-mono bg-slate-600">#{index + 1}</Badge>
                           <span className="text-xs font-bold text-slate-700">{perm.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {perm.likelihoodScore && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-[9px] text-slate-500 uppercase tracking-wide">Score</span>
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] font-mono h-5 px-1.5",
                                  perm.likelihoodScore.compositeScore >= 30 ? "bg-emerald-50 border-emerald-300 text-emerald-700" :
                                  perm.likelihoodScore.compositeScore >= 20 ? "bg-blue-50 border-blue-300 text-blue-700" :
                                  perm.likelihoodScore.compositeScore >= 10 ? "bg-amber-50 border-amber-300 text-amber-700" :
                                  "bg-slate-50 border-slate-300 text-slate-600"
                                )}
                              >
                                {perm.likelihoodScore.compositeScore.toFixed(2)}
                              </Badge>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-3 h-3 text-slate-400 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <div className="text-xs space-y-1">
                                    <div className="font-semibold">Composite Log-Likelihood</div>
                                    <div className="text-slate-500">Σ log₁₀(LR) = {perm.likelihoodScore.compositeScore.toFixed(4)}</div>
                                    <div className="text-slate-400 text-[10px] pt-1">
                                      {perm.likelihoodScore.edgeCount} relationship{perm.likelihoodScore.edgeCount !== 1 ? 's' : ''} scored
                                    </div>
                                    {perm.likelihoodScore.assertedRelationships.length > 0 && (
                                      <div className="pt-1 border-t border-slate-200 mt-1">
                                        {perm.likelihoodScore.assertedRelationships.slice(0, 4).map((rel: { relationshipType: string; individual1: string; individual2: string; logLR: number }, i: number) => (
                                          <div key={i} className="text-[9px] text-slate-500">
                                            {rel.relationshipType}: {rel.individual1.split(' ')[0]}-{rel.individual2.split(' ')[0]} = {rel.logLR.toFixed(2)}
                                          </div>
                                        ))}
                                        {perm.likelihoodScore.assertedRelationships.length > 4 && (
                                          <div className="text-[9px] text-slate-400">+{perm.likelihoodScore.assertedRelationships.length - 4} more</div>
                                        )}
                                      </div>
                                    )}
                                    <div className="text-[9px] text-amber-600 pt-1">⚠ Approximate ranking (pairwise LRs not independent)</div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1 relative overflow-x-auto p-4">
                         {/* Handle special rendering for Disjointed template */}
                         <div className="min-w-max flex items-center justify-center">
                           <TreeErrorBoundary permLabel={perm.label}>
                             {perm.type === 'Disjointed' || (perm.nodesB) ? (
                                <div className="flex gap-16">
                                    <div className="relative w-[350px] h-[500px]">
                                        <ReactFamilyTree nodes={perm.nodes as any} rootId={perm.rootId} width={WIDTH} height={HEIGHT} renderNode={(node) => (
                                           <ReactFamilyNode key={node.id} node={node} style={{ width: WIDTH, height: HEIGHT, transform: `translate(${node.left * (WIDTH / 2)}px, ${node.top * (HEIGHT / 2)}px)`, position: 'absolute' }} />
                                        )} />
                                    </div>
                                    <div className="relative w-[450px] h-[500px]">
                                        <ReactFamilyTree nodes={perm.nodesB as any} rootId={perm.rootIdB} width={WIDTH} height={HEIGHT} renderNode={(node) => (
                                           <ReactFamilyNode key={node.id} node={node} style={{ width: WIDTH, height: HEIGHT, transform: `translate(${node.left * (WIDTH / 2)}px, ${node.top * (HEIGHT / 2)}px)`, position: 'absolute' }} />
                                        )} />
                                    </div>
                                </div>
                             ) : (
                                <div className="relative w-[500px] h-[500px]">
                                    <ReactFamilyTree nodes={perm.nodes as any} rootId={perm.rootId} width={WIDTH} height={HEIGHT} renderNode={(node) => (
                                       <ReactFamilyNode key={node.id} node={node} style={{ width: WIDTH, height: HEIGHT, transform: `translate(${node.left * (WIDTH / 2)}px, ${node.top * (HEIGHT / 2)}px)`, position: 'absolute' }} />
                                    )} />
                                </div>
                             )}
                           </TreeErrorBoundary>
                         </div>
                      </div>
                   </div>
                ))}
            </div>
        </div>
      )}
    </div>
  );
}

export function PedigreeAnalysisView({ record }: { record: KinshipRecord }) {
  const isParentChildPlausible = record.parentChildLR > 100;
  const isSiblingPlausible = record.fullSiblingLR > 100;

  return (
    <div className="space-y-8 p-2">
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-border pb-2">
          <h3 className="font-semibold text-lg">Pairwise Combinations</h3>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">Pass 1</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={cn(
            "border rounded-xl overflow-hidden transition-all",
            isParentChildPlausible ? "border-border shadow-sm" : "border-border/40 opacity-60"
          )}>
            <div className="bg-muted/30 p-3 border-b border-border flex justify-between items-center">
              <span className="font-medium text-sm">Parent/Child Hypothesis</span>
              <span className="text-xs font-mono">LR: {record.parentChildLR.toExponential(2)}</span>
            </div>
            <div className="p-6 flex justify-center bg-slate-50/50">
               <ParentChildTree 
                 parent={{ name: record.relative1.name, gender: record.relative1.gender, isIndex: true }}
                 child={{ name: record.relative2.name, gender: record.relative2.gender, isIndex: true }}
               />
            </div>
          </div>

          <div className={cn(
            "border rounded-xl overflow-hidden transition-all",
            isSiblingPlausible ? "border-border shadow-sm" : "border-border/40 opacity-60"
          )}>
            <div className="bg-muted/30 p-3 border-b border-border flex justify-between items-center">
              <span className="font-medium text-sm">Full Sibling Hypothesis</span>
              <span className="text-xs font-mono">LR: {record.fullSiblingLR.toExponential(2)}</span>
            </div>
            <div className="p-6 flex justify-center bg-slate-50/50">
              <FullSiblingTree 
                 sibling1={{ name: record.relative1.name, gender: record.relative1.gender, isIndex: true }}
                 sibling2={{ name: record.relative2.name, gender: record.relative2.gender, isIndex: true }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
